import { useTranslation } from '@multaqa/i18n'
import { Button } from '@multaqa/ui/components/ui/button'
import { Panel, PanelContent, PanelHeader, PanelTitle } from '@multaqa/ui/components/ui/panel'
import { toast } from '@multaqa/ui/components/ui/sonner'
import { PencilIcon } from 'lucide-react'
import { useState } from 'react'
import { formatErrorMessage } from '@/lib/error-message'
import type { GroupDefaults as GroupDefaultsData } from './api'
import { useUpdateGroupDefaults } from './api'
import { SettingsFields } from './group-defaults-settings-fields'
import type { SettingsFormValues } from './group-defaults-settings-view'
import { emptyDefaults, fromDefaults, SettingsReadOnly } from './group-defaults-settings-view'

export function SettingsCard({ groupId, data }: { groupId: string; data: GroupDefaultsData }) {
  const { t } = useTranslation()
  const update = useUpdateGroupDefaults()
  const [editing, setEditing] = useState(false)
  const [values, setValues] = useState<SettingsFormValues>(() => fromDefaults(data))
  const resetValues = () => setValues(fromDefaults(data))
  const save = () =>
    update.mutate(
      { groupId, input: values },
      {
        onSuccess: () => {
          setEditing(false)
          toast.success(t('groups.defaults.settings.saved'))
        },
        onError: (error) =>
          toast.error(formatErrorMessage(error, t('groups.defaults.errors.generic'))),
      },
    )
  const reset = () =>
    update.mutate(
      { groupId, input: emptyDefaults() },
      {
        onSuccess: () => {
          resetValues()
          setEditing(false)
          toast.success(t('groups.defaults.settings.resetSaved'))
        },
        onError: (error) =>
          toast.error(formatErrorMessage(error, t('groups.defaults.errors.generic'))),
      },
    )
  return (
    <Panel data-testid="group-defaults-settings-card">
      <PanelHeader>
        <PanelTitle>{t('groups.defaults.settings.title')}</PanelTitle>
        <div className="ms-auto flex flex-wrap items-center justify-end gap-2">
          {editing ? (
            <>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={update.isPending}
                onClick={() => {
                  resetValues()
                  setEditing(false)
                }}
                data-testid="group-defaults-settings-cancel"
              >
                {t('groups.defaults.settings.cancel')}
              </Button>
              <Button
                type="button"
                size="sm"
                loading={update.isPending}
                disabled={update.isPending}
                onClick={save}
                data-testid="group-defaults-settings-save"
              >
                {t('groups.defaults.settings.save')}
              </Button>
            </>
          ) : (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                resetValues()
                setEditing(true)
              }}
              data-testid="group-defaults-settings-edit"
            >
              <PencilIcon className="size-3.5" aria-hidden="true" />
              {t('groups.defaults.settings.edit')}
            </Button>
          )}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            loading={update.isPending && !editing}
            disabled={update.isPending}
            onClick={reset}
            data-testid="group-defaults-settings-reset"
          >
            {t('groups.defaults.settings.reset')}
          </Button>
        </div>
      </PanelHeader>
      <PanelContent className="p-4">
        {editing ? (
          <SettingsFields
            values={values}
            system={data.system_defaults}
            disabled={update.isPending}
            onChange={(key, value) => setValues((current) => ({ ...current, [key]: value }))}
          />
        ) : (
          <SettingsReadOnly data={data} />
        )}
      </PanelContent>
    </Panel>
  )
}
